import { useCallback, useEffect, useMemo, useState } from "react";
import {
  loadPlatformProfile,
  loadPlatformSubscriptions,
  startPlatformSubscription,
  updatePlatformSubscription,
  type PlatformSubscriptionRow,
  type PlatformSubscriptionStatus,
} from "../platform/platformSubscriptions";
import { supabase } from "../auth/supabaseClient";

function statusLabel(value: PlatformSubscriptionStatus): string {
  if (value === "active") return "فعال";
  if (value === "suspended") return "تعلیق";
  if (value === "cancelled") return "لغوشده";
  if (value === "expired") return "منقضی";
  return "بدون اشتراک";
}

function jalaliDate(value?: string): string {
  if (!value) return "—";
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("fa-IR-u-ca-persian");
}

export function PlatformSubscriptionPanel() {
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [checked, setChecked] = useState(false);
  const [rows, setRows] = useState<PlatformSubscriptionRow[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState("");
  const [days, setDays] = useState(30);
  const [planCode, setPlanCode] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    setError(undefined);
    try {
      const profile = await loadPlatformProfile(supabase);
      setIsPlatformOwner(profile.isPlatformOwner);
      if (!profile.isPlatformOwner) {
        setRows([]);
        return;
      }

      const next = await loadPlatformSubscriptions(supabase);
      setRows(next);
      setSelectedFarmId((current) =>
        next.some((row) => row.farmId === current) ? current : (next[0]?.farmId ?? ""),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری اشتراک‌های سالن انجام نشد.");
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selected = useMemo(
    () => rows.find((row) => row.farmId === selectedFarmId),
    [rows, selectedFarmId],
  );

  if (!checked || !isPlatformOwner) return null;

  async function run(action: () => Promise<void>, successMessage: string) {
    try {
      setBusy(true);
      setError(undefined);
      setMessage(undefined);
      await action();
      setMessage(successMessage);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "عملیات اشتراک انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section-card" aria-labelledby="platform-subscriptions-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">مالک پلتفرم</p>
          <h2 id="platform-subscriptions-title">اشتراک سالن‌ها</h2>
        </div>
        <button
          type="button"
          className="text-button"
          disabled={busy}
          onClick={() => void refresh()}
        >
          بروزرسانی
        </button>
      </div>

      <p className="section-note">
        هر سالن یک اشتراک مستقل دارد. اطلاعات سالن‌ها با هم ترکیب نمی‌شوند و فقط مالک پلتفرم می‌تواند
        مدت استفاده را فعال، تمدید یا تعلیق کند.
      </p>

      <div className="work-list">
        {rows.map((row) => (
          <button
            type="button"
            className="work-item pending"
            key={row.farmId}
            onClick={() => setSelectedFarmId(row.farmId)}
          >
            <span className="work-main">
              <strong>{row.farmName}</strong>
              <span>
                {row.ownerName} · {statusLabel(row.status)}
                {row.subscriptionCode ? ` · ${row.subscriptionCode}` : ""}
              </span>
            </span>
            <span className="work-status">
              {row.daysRemaining === undefined ? "—" : `${row.daysRemaining} روز`}
            </span>
          </button>
        ))}
      </div>

      {selected ? (
        <article className="manager-card">
          <div className="section-title-row">
            <div>
              <p className="section-kicker">سالن انتخاب‌شده</p>
              <h3>{selected.farmName}</h3>
            </div>
            <span className="count-pill">{statusLabel(selected.status)}</span>
          </div>

          <p className="section-note">
            مدیر/مالک سالن: {selected.ownerName} · شروع: {jalaliDate(selected.startsOn)} · پایان:{" "}
            {jalaliDate(selected.expiresOn)}
          </p>

          {selected.status === "unmanaged" ? (
            <p className="settings-warning">
              این سالن هنوز رکورد اشتراک ندارد و برای سازگاری مهاجرت فعلاً دسترسی آن آزاد است.
            </p>
          ) : null}

          <div className="operation-field-grid">
            <label>
              پلن
              <input value={planCode} onChange={(event) => setPlanCode(event.target.value)} />
            </label>
            <label>
              مدت (روز)
              <input
                type="number"
                min="1"
                max="730"
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
              />
            </label>
          </div>

          <div className="confirm-actions">
            {[30, 90, 180, 365].map((value) => (
              <button
                type="button"
                className="text-button"
                key={value}
                disabled={busy}
                onClick={() => setDays(value)}
              >
                {value} روز
              </button>
            ))}
          </div>

          {selected.status === "unmanaged" ? (
            <button
              type="button"
              className="secondary-button"
              disabled={busy}
              onClick={() =>
                void run(
                  () => startPlatformSubscription(supabase, selected.farmId, planCode, days),
                  "اشتراک سالن فعال شد.",
                )
              }
            >
              شروع اشتراک
            </button>
          ) : (
            <div className="confirm-actions">
              <button
                type="button"
                className="secondary-button"
                disabled={busy || !selected.subscriptionId}
                onClick={() =>
                  selected.subscriptionId
                    ? void run(
                        () =>
                          updatePlatformSubscription(
                            supabase,
                            selected.subscriptionId as string,
                            "renew",
                            days,
                          ),
                        "اشتراک تمدید و فعال شد.",
                      )
                    : undefined
                }
              >
                تمدید
              </button>
              {selected.status === "suspended" ? (
                <button
                  type="button"
                  className="text-button"
                  disabled={busy || !selected.subscriptionId}
                  onClick={() =>
                    selected.subscriptionId
                      ? void run(
                          () =>
                            updatePlatformSubscription(
                              supabase,
                              selected.subscriptionId as string,
                              "activate",
                            ),
                          "اشتراک دوباره فعال شد.",
                        )
                      : undefined
                  }
                >
                  فعال‌سازی
                </button>
              ) : (
                <button
                  type="button"
                  className="text-button"
                  disabled={busy || !selected.subscriptionId}
                  onClick={() =>
                    selected.subscriptionId
                      ? void run(
                          () =>
                            updatePlatformSubscription(
                              supabase,
                              selected.subscriptionId as string,
                              "suspend",
                            ),
                          "اشتراک تعلیق شد؛ اطلاعات سالن حفظ شده است.",
                        )
                      : undefined
                  }
                >
                  تعلیق
                </button>
              )}
            </div>
          )}
        </article>
      ) : null}

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}
    </section>
  );
}
